import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import { ActionMenuItem, Button, ConfirmDialog, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

/**
 * "Mark email verified" -- for someone who never received their code.
 *
 * Until the email is verified the server holds most pages back from the
 * account (and from an admin signed in as it), so this is how support gets a
 * stuck account working. It does exactly what entering the code would.
 *
 * `account` is `{ id, full_name, role, status, is_email_verified? }`. Rows that
 * do not carry `is_email_verified` fall back to the status the server gives an
 * unverified account.
 */
/**
 * `compact`: a small "Verify email" for a card footer.
 * `menu`: an item of the surrounding `ActionMenu` ("More").
 */
export function VerifyEmailAction({ account, size = 'sm', compact = false, menu = false }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const verify = useMutation({
    mutationFn: () => adminApi.verifyUserEmail(account.id),
    onSuccess: (result) => {
      setOpen(false);
      toast.success(result?.message ?? 'Email address marked as verified');
      void queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
      });
    },
    onError: (error) => {
      setOpen(false);
      toast.error(errorMessage(error, 'Could not mark this email as verified'));
    },
  });

  const unverified =
    account?.is_email_verified === false || account?.status === 'PENDING_VERIFICATION';
  if (!account?.id || account.role === 'SUPER_ADMIN' || !unverified) return null;

  return (
    <>
      {menu ? (
        <ActionMenuItem icon="userCheck" onSelect={() => setOpen(true)}>
          Mark email verified
        </ActionMenuItem>
      ) : compact ? (
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          <Icon name="userCheck" size={14} />
          Verify email
        </Button>
      ) : (
        <Button variant="tintBrand" size={size} onClick={() => setOpen(true)}>
          <Icon name="userCheck" size={15} />
          Mark email verified
        </Button>
      )}

      <ConfirmDialog
        open={open}
        tone="primary"
        title={`Mark ${account.full_name}'s email as verified?`}
        message={
          <>
            Do this only when you know the email address belongs to them. They will be able to use
            their account straight away, without entering a code. This is recorded in the audit log.
          </>
        }
        confirmLabel="Mark as verified"
        isLoading={verify.isPending}
        onConfirm={() => verify.mutate()}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
